import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { ChatDocumentLocatorSchema, isAppFileDocument } from "@bliks-io/contracts";
import { Attachment, AttachmentAction, AttachmentActions, AttachmentContent, AttachmentDescription, AttachmentMedia, AttachmentTitle } from "../chat/attachment";
import { ToolPartBlock } from "./tool-part-block";
import { ChatPartNotice } from "./chat-part-shell";
function ChatDocumentCard({ output, download }) {
    const { t, i18n } = useTranslation();
    const [downloadState, setDownloadState] = useState("idle");
    /**
   * Two hops on purpose, both the host's: the app endpoint authorizes the
   * caller and answers with a short-lived presigned URL, which the browser then
   * fetches WITHOUT an `Authorization` header — the object store rejects a
   * request carrying both a SigV4 signature and that header. This card owns
   * only which of the three outcomes the reader is shown.
   */ const startDownload = async ()=>{
        setDownloadState("loading");
        const result = await download(output);
        setDownloadState(result === "done" ? "idle" : result);
    };
    const failedToDownload = downloadState === "expired" || downloadState === "failed";
    /**
   * An app file has no retention deadline to quote — the only expiry that
   * exists is the one the download hop's own response reports for the link it
   * just minted — so the card says how many rows it holds and stops there. The
   * legacy export does carry a retention deadline, and keeps saying it.
   */ const describeFile = ()=>{
        const rows = new Intl.NumberFormat(i18n.language).format(output.rowCount);
        if (isAppFileDocument(output)) {
            return t(output.truncated ? "chat.document.partialRowsOnly" : "chat.document.rowsOnly", {
                rows
            });
        }
        return t(output.truncated ? "chat.document.partialRows" : "chat.document.rows", {
            rows,
            expiry: new Intl.DateTimeFormat(i18n.language, {
                dateStyle: "short",
                timeStyle: "short"
            }).format(new Date(output.expiresAt))
        });
    };
    const description = failedToDownload ? t(downloadState === "expired" ? "chat.document.expired" : "chat.document.downloadFailed") : describeFile();
    return /*#__PURE__*/ _jsxs("div", {
        className: "w-full min-w-0",
        children: [
            /*#__PURE__*/ _jsxs(Attachment, {
                state: failedToDownload ? "error" : "done",
                className: "max-w-full",
                children: [
                    /*#__PURE__*/ _jsx(AttachmentMedia, {
                        children: /*#__PURE__*/ _jsx(FileSpreadsheet, {
                            "aria-hidden": true
                        })
                    }),
                    /*#__PURE__*/ _jsxs(AttachmentContent, {
                        children: [
                            /*#__PURE__*/ _jsx(AttachmentTitle, {
                                children: output.filename
                            }),
                            /*#__PURE__*/ _jsx(AttachmentDescription, {
                                children: description
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(AttachmentActions, {
                        children: /*#__PURE__*/ _jsx(AttachmentAction, {
                            "aria-label": t("chat.document.download"),
                            disabled: downloadState === "loading" || downloadState === "expired",
                            onClick: startDownload,
                            children: downloadState === "loading" ? /*#__PURE__*/ _jsx(Loader2, {
                                className: "animate-spin",
                                "aria-hidden": true
                            }) : /*#__PURE__*/ _jsx(Download, {
                                "aria-hidden": true
                            })
                        })
                    })
                ]
            }),
            output.omitted > 0 && // The workbook is treated as authoritative once downloaded, so rows the
            // caller may not read have to be declared here — the same honesty
            // `CoverageNote` gives a chart.
            /*#__PURE__*/ _jsx("p", {
                className: "mt-2 text-xs text-muted-foreground",
                children: t("chat.document.omitted", {
                    omitted: new Intl.NumberFormat(i18n.language).format(output.omitted)
                })
            })
        ]
    });
}
/** Placeholder while the model is still assembling the export's parameters. */ function ChatDocumentSkeleton() {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsxs(Attachment, {
        state: "processing",
        role: "status",
        "aria-label": t("chat.document.preparing"),
        children: [
            /*#__PURE__*/ _jsx(AttachmentMedia, {
                children: /*#__PURE__*/ _jsx(FileSpreadsheet, {
                    "aria-hidden": true
                })
            }),
            /*#__PURE__*/ _jsx(AttachmentContent, {
                children: /*#__PURE__*/ _jsx(AttachmentTitle, {
                    children: t("chat.document.preparing")
                })
            })
        ]
    });
}
/**
 * An export that could not be produced, or whose payload does not hold. Nothing
 * is persisted for it, so it disappears on reload.
 */ function ChatDocumentError() {
    const { t } = useTranslation();
    return /*#__PURE__*/ _jsx(ChatPartNotice, {
        message: t("chat.document.failed")
    });
}
/** Draws whatever state a `generateDocument` part is in. */ export function ChatDocumentBlock({ part, download }) {
    return /*#__PURE__*/ _jsx(ToolPartBlock, {
        part: part,
        schema: ChatDocumentLocatorSchema,
        skeleton: /*#__PURE__*/ _jsx(ChatDocumentSkeleton, {}),
        error: /*#__PURE__*/ _jsx(ChatDocumentError, {}),
        children: (output)=>/*#__PURE__*/ _jsx(ChatDocumentCard, {
                output: output,
                download: download
            })
    });
}

//# sourceMappingURL=chat-document.js.map